import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useEffect, useState, useRef, useMemo } from "react";

import SpotSheet from "../Spots/SpotSheet";
import AddSpotForm from "../Spots/AddSpotForm";
import { supabase } from "../supabaseClient";

import Header from "../UI/Header";
import Footer from "../UI/Footer";
import Menu from "../UI/Menu";
import MapMarkers from "./MapMarkers";
import MapControls from "./MapControls";
import AuthPage from "../Auth/AuthPage";





/*
|--------------------------------------------------------------------------
| GOOGLE MAP CONFIGURATION
|--------------------------------------------------------------------------
| Advanced markers require the "marker" library.
| Keeping this outside prevents reloading the map script.
*/
const libraries = ["marker"];


/*
|--------------------------------------------------------------------------
| MAP CONTAINER STYLE
|--------------------------------------------------------------------------
| Controls the map element size.
*/
const containerStyle = {
  width: "100%",
  height: "100%",
};


/*
|--------------------------------------------------------------------------
| DEFAULT MAP CENTER
|--------------------------------------------------------------------------
| Used if location permission is denied.
*/
const fallbackCenter = {
  lat: 34.0522,
  lng: -118.2437,
};


/*
|--------------------------------------------------------------------------
| MAP FRAME STYLE
|--------------------------------------------------------------------------
| Glass-style container holding the map.
*/
const mapFrame = {
  position: "fixed",
  top: "20px",
  bottom: "20px",
  left: "10px",
  right: "10px",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
};


/*
|--------------------------------------------------------------------------
| MAIN MAP COMPONENT
|--------------------------------------------------------------------------
| Central controller for the map page.
*/
function MapView({ user }) {

  /*
  |--------------------------------------------------------------------------
  | LOAD GOOGLE MAPS SCRIPT
  |--------------------------------------------------------------------------
  */
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries,
  });


  /*
  |--------------------------------------------------------------------------
  | UI STATE
  |--------------------------------------------------------------------------
  | Controls interface panels.
  */

  const [showAuth, setShowAuth] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(null);

  const [isAddingSpot, setIsAddingSpot] = useState(false);
  const [newSpotLocation, setNewSpotLocation] = useState(null);



  /*
  |--------------------------------------------------------------------------
  | MAP STATE
  |--------------------------------------------------------------------------
  | Controls map position and user location.
  */

  const [center, setCenter] = useState(fallbackCenter);
  const [userLocation, setUserLocation] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);

  const [hasCentered, setHasCentered] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | SPOT DATA
  |--------------------------------------------------------------------------
  | Stores spots pulled from the database.
  */

  const [spots, setSpots] = useState([]);

  const [visibleSpots, setVisibleSpots] = useState([]);


  /*
  |--------------------------------------------------------------------------
  | MAP REFERENCE
  |--------------------------------------------------------------------------
  | Allows programmatic map control.
  */

  const mapRef = useRef(null);
  const boundsTimeout = useRef(null);



  /*
  |--------------------------------------------------------------------------
  | USER LOCATION WATCHER
  |--------------------------------------------------------------------------
  | Continuously updates the user's location.
  | Map only centers the first time.
  */

  useEffect(() => {

    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(

      (position) => {

        const userPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        setUserLocation(userPos);

        if (!hasCentered) {
          setCenter(userPos);
          setHasCentered(true);
        }

      },

      (error) => {
        console.log("Location error:", error);
      },

      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000,
      }

    );

    return () => navigator.geolocation.clearWatch(watchId);

  }, [hasCentered]);

  



  /*
  |--------------------------------------------------------------------------
  | DISTANCE CALCULATION
  |--------------------------------------------------------------------------
  | Haversine formula to compute miles between points.
  */

  const getDistanceMiles = (lat1, lon1, lat2, lon2) => {

    const R = 3958.8;

    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };


  /*
  |--------------------------------------------------------------------------
  | NEARBY SPOT GENERATION
  |--------------------------------------------------------------------------
  | Adds distance values and sorts closest spots.
  */

const nearbySpots = useMemo(() => {

  if (!userLocation) return [];

  return spots
    .map((spot) => ({
      ...spot,
      distance: getDistanceMiles(
        userLocation.lat,
        userLocation.lng,
        spot.lat,
        spot.lng
      ),
    }))
    .filter((spot) => spot.distance <= 5)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 10);

}, [spots, userLocation]);



  /*
  |--------------------------------------------------------------------------
  | LOAD SPOTS FROM SUPABASE
  |--------------------------------------------------------------------------
  */

 useEffect(() => {

const fetchSpots = async () => {

  const { data, error } = await supabase
    .from("spots")
    .select(`
      *,
      profiles!left (
        username
      )
    `);

  if (!error) {

    const normalized = data.map((spot) => ({
      ...spot,
      username: spot.profiles?.username || "Original Spot"
    }));

    setSpots(normalized);
    setVisibleSpots(normalized);

  }

};

  fetchSpots();

  

}, []);

  /*
  |--------------------------------------------------------------------------
  | RECENTER MAP
  |--------------------------------------------------------------------------
  | Used by the recenter button.
  */

  const recenterMap = () => {

    if (!userLocation || !mapRef.current) return;

    mapRef.current.panTo(userLocation);

    setTimeout(() => {
      mapRef.current.setZoom(15);
    }, 200);

  };

  useEffect(() => {

  const params = new URLSearchParams(window.location.search);

  const sharedSpotId = params.get("spot");

  if (!sharedSpotId) return;

  const foundSpot = spots.find(
    (s) => String(s.id) === sharedSpotId
  );

 if (foundSpot) {

  setSelectedSpot(foundSpot);

  setCenter({
    lat: foundSpot.lat,
    lng: foundSpot.lng
  });

  if (mapRef.current) {

    mapRef.current.panTo({
      lat: foundSpot.lat,
      lng: foundSpot.lng
    });

    mapRef.current.setZoom(16);

  }

   window.history.replaceState(
    {},
    "",
    window.location.pathname
  );

}

}, [spots, mapInstance]);



  /*
  |--------------------------------------------------------------------------
  | LOADING STATES
  |--------------------------------------------------------------------------
  */

  if (loadError) return <div>Map failed to load</div>;
  if (!isLoaded) return <div>Loading map…</div>;



  /*
  |--------------------------------------------------------------------------
  | RENDER MAP PAGE
  |--------------------------------------------------------------------------
  */

  return (
    <>

      {/* Header / Logo */}
      <Header />

      {/* Hamburger Menu */}
      <Menu
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        nearbySpots={nearbySpots}
        spots={spots}
        setSelectedSpot={setSelectedSpot}
        mapRef={mapRef}
      />


      {/* Map Container */}
      <div style={mapFrame}>

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={15}

          onBoundsChanged={() => {

            if (isAddingSpot) return;

            if (!mapRef.current) return;

            clearTimeout(boundsTimeout.current);

            boundsTimeout.current = setTimeout(() => {

            const bounds = mapRef.current.getBounds();

            if (!bounds) return;

            const filtered = spots.filter((spot) => {

            return bounds.contains(
            new window.google.maps.LatLng(spot.lat, spot.lng)
           );

           });

          setVisibleSpots(filtered);

             }, 100);

           }}

          onClick={(e) => {

            if (isAddingSpot) {

              const lat = e.latLng.lat();
              const lng = e.latLng.lng();

              setNewSpotLocation({ lat, lng });
              return;

            }

            if (selectedSpot) {
              setSelectedSpot(null);
            }

          }}

          onLoad={(map) => {
            mapRef.current = map;
            setMapInstance(map);
          }}

          options={{
            mapId: "c807dd6641c887195f661b90",
            disableDefaultUI: true,
            zoomControl: true,
          }}

        >

          {/* User Location Marker */}
          {userLocation && (
            <>
              <Marker
                position={userLocation}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 22,
                  fillColor: "#1a73e8",
                  fillOpacity: 0.15,
                  strokeOpacity: 0,
                }}
              />

              <Marker
                position={userLocation}
                zIndex={1}
                icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: "#1a73e8",
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 2,
               }}
              />
            </>
          )}

          {/* Spot Markers */}
          <MapMarkers
            mapInstance={mapInstance}
            spots={visibleSpots}
            setSelectedSpot={setSelectedSpot}
          />

          {/* Add Spot Preview Marker */}
          {newSpotLocation && (
            <Marker
              position={newSpotLocation}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
              }}
            />
          )}

        </GoogleMap>

      </div>


      {/* Footer */}
      <Footer />


      {/* Map Control Buttons */}
      <MapControls
        recenterMap={recenterMap}
        startAdding={() => {

          if (!user) {
           setShowAuth(true);
          return;
          }

         setIsAddingSpot(true);
         setSelectedSpot(null);

        }}
      />


      {/* Spot Details Panel */}
      {selectedSpot && (
        <SpotSheet
          spot={selectedSpot}
          spots={spots}
          setSpots={setSpots}
          setSelectedSpot={setSelectedSpot}
          visibleSpots={visibleSpots}
          setVisibleSpots={setVisibleSpots}
          userLocation={userLocation}
          user={user}
          setShowAuth={setShowAuth}
          onClose={() => setSelectedSpot(null)}
        />
      )}

    {showAuth && (
      <AuthPage onClose={() => setShowAuth(false)} />
    )}

      {/* Add Spot Form */}
      {newSpotLocation && isAddingSpot && (
        <AddSpotForm
          location={newSpotLocation}

         onSave={async (newSpot) => {

  const { data, error } = await supabase
    .from("spots")
    .insert([
      {
        ...newSpot,
        user_id: user.id
      }
    ])
    .select();

  if (error) {
    console.error("Error saving:", error);
    return;
  }

  /* fetch username from profiles */
  const { data: profile } = await supabase
    .from("profiles")
    .select("username")
    .eq("id", user.id)
    .single();

  const newSpotWithUser = {
    ...data[0],
    username: profile?.username
  };

  setSpots(prev => [...prev, newSpotWithUser]);
  setVisibleSpots(prev => [...prev, newSpotWithUser]);

  setIsAddingSpot(false);
  setNewSpotLocation(null);

}}

          onCancel={() => {
            setIsAddingSpot(false);
            setNewSpotLocation(null);
          }}

        />
      )}

    </>
  );
}

export default MapView;